import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Building2, ChevronRight, Eye, MapPin, Mail, Pencil, Phone, Plus, Search, X
} from 'lucide-react';
import { readToken, tenantTokenKey } from '../../app/session';
import { MobileVirtualList, useMobileViewport } from '../../components/MobileVirtualList';
import { SwipeReveal } from '../../components/SwipeReveal';
import { ApiError, apiRequest } from '../../lib/api';
import { useTranslation } from '../../i18n/i18n';
import { useTenantApp } from '../app/TenantAppContext';

interface Partner {
  id: string; partnerCode: string; name: string; oib: string | null;
  address: string | null; postalCode: string | null; city: string | null;
  countryCode: string; contactName: string | null; email: string | null;
  phone: string | null; notes: string | null; status: 'ACTIVE' | 'INACTIVE';
  locationCount: number; openTasks: number; openWorkOrders: number;
}

interface Location {
  id: string; name: string; address: string; postalCode: string | null;
  city: string; contactName: string | null; contactPhone: string | null;
}

const emptyPartner = {
  partnerCode: '', name: '', oib: '', address: '', postalCode: '', city: '',
  countryCode: 'HR', contactName: '', email: '', phone: '', notes: '', status: 'ACTIVE'
};
const emptyLocation = {
  name: '', address: '', postalCode: '', city: '', contactName: '', contactPhone: ''
};

export function PartnersPage() {
  const { context } = useTenantApp();
  const { t } = useTranslation();
  const canManage = context.user.permissions.includes('partners.manage');
  const isMobile = useMobileViewport();
  const [items, setItems] = useState<Partner[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<Partner | null>(null);
  const [form, setForm] = useState(emptyPartner);
  const [detail, setDetail] = useState<Partner | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationForm, setLocationForm] = useState(emptyLocation);
  const [locationModal, setLocationModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    try {
      const query = new URLSearchParams({ search, status });
      const response = await apiRequest<{ items: Partner[] }>(`/api/v1/partners?${query}`, {
        accessToken: readToken(tenantTokenKey)
      });
      setItems(response.items);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Partneri nisu dostupni.');
    }
  }

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 150);
    return () => window.clearTimeout(timer);
  }, [search, status]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await apiRequest(editing ? `/api/v1/partners/${editing.id}` : '/api/v1/partners', {
        method: editing ? 'PATCH' : 'POST',
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify(form)
      });
      setModal(false);
      setEditing(null);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Partnera nije moguće spremiti.');
    } finally {
      setBusy(false);
    }
  }

  function open(partner?: Partner) {
    setEditing(partner ?? null);
    setForm(partner ? {
      partnerCode: partner.partnerCode, name: partner.name, oib: partner.oib ?? '',
      address: partner.address ?? '', postalCode: partner.postalCode ?? '',
      city: partner.city ?? '', countryCode: partner.countryCode,
      contactName: partner.contactName ?? '', email: partner.email ?? '',
      phone: partner.phone ?? '', notes: partner.notes ?? '', status: partner.status
    } : { ...emptyPartner, partnerCode: `PAR-${String(items.length + 1).padStart(3, '0')}` });
    setError('');
    setModal(true);
  }

  async function showDetail(partner: Partner) {
    setDetail(partner);
    const response = await apiRequest<{ items: Location[] }>(
      `/api/v1/partners/${partner.id}/locations`,
      { accessToken: readToken(tenantTokenKey) }
    );
    setLocations(response.items);
  }

  async function saveLocation(event: FormEvent) {
    event.preventDefault();
    if (!detail) return;
    setBusy(true);
    try {
      await apiRequest(`/api/v1/partners/${detail.id}/locations`, {
        method: 'POST', accessToken: readToken(tenantTokenKey),
        body: JSON.stringify(locationForm)
      });
      setLocationModal(false);
      setLocationForm(emptyLocation);
      await showDetail(detail);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Lokaciju nije moguće spremiti.');
    } finally { setBusy(false); }
  }

  const activeCount = useMemo(() => items.filter((item) => item.status === 'ACTIVE').length, [items]);

  return (
    <div className="module-page">
      <header className="module-page__hero">
        <div><span className="eyebrow">ŠIFARNIK · KLIJENTI</span><h1>{t('partners.title')}</h1><p>{t('partners.subtitle')}</p></div>
        {canManage && <button className="button button--primary" data-mobile-fab onClick={() => open()} aria-label="Novi partner"><Plus /> Novi partner</button>}
      </header>
      <section className="task-summary">
        <article><Building2 /><div><strong>{activeCount}</strong><small>Aktivnih partnera</small></div></article>
        <article><MapPin /><div><strong>{items.reduce((sum, item) => sum + item.locationCount, 0)}</strong><small>Lokacija</small></div></article>
        <article><Phone /><div><strong>{items.filter((item) => item.phone).length}</strong><small>Kontakata</small></div></article>
      </section>
      <section className="data-card">
        <header className="data-toolbar">
          <label className="compact-search"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Naziv, OIB, mjesto ili kontakt…" /></label>
          <select className="compact-select" value={status} onChange={(event) => setStatus(event.target.value)}><option value="ACTIVE">Aktivni</option><option value="INACTIVE">Neaktivni</option><option value="ALL">Svi</option></select>
        </header>
        {error && <div className="form-error">{error}</div>}
        {!isMobile && <div className="business-table-wrap partner-desktop-list">
          <table className="business-table partner-table">
            <thead><tr><th>Partner</th><th>OIB</th><th>Mjesto</th><th>Kontakt</th><th>Aktivnosti</th><th>Status</th><th><span className="sr-only">Akcije</span></th></tr></thead>
            <tbody>
              {items.map((partner) => (
                <tr key={partner.id} tabIndex={0} onClick={() => canManage ? open(partner) : void showDetail(partner)} onKeyDown={(event) => { if (event.key === 'Enter') canManage ? open(partner) : void showDetail(partner); }}>
                  <td><span className="partner-table__identity"><span className="partner-avatar">{partner.name.slice(0, 2).toUpperCase()}</span><span><strong>{partner.name}</strong><small>{partner.partnerCode}</small></span></span></td>
                  <td><span className="table-primary">{partner.oib || '—'}</span></td>
                  <td><span className="table-primary">{partner.city || '—'}</span><small className="table-secondary">{[partner.address, partner.postalCode].filter(Boolean).join(', ') || 'Adresa nije upisana'}</small></td>
                  <td><span className="table-primary">{partner.contactName || '—'}</span><small className="table-secondary">{partner.phone || partner.email || 'Nema kontakta'}</small></td>
                  <td><span className="partner-table__metrics"><span>{partner.locationCount}<small>lokacija</small></span><span>{partner.openTasks}<small>zadataka</small></span><span>{partner.openWorkOrders}<small>naloga</small></span></span></td>
                  <td><span className={`status-pill status-pill--${partner.status.toLowerCase()}`}>{partner.status === 'ACTIVE' ? 'Aktivan' : 'Neaktivan'}</span></td>
                  <td><span className="business-table__actions"><button className="icon-button" title="Detalji i lokacije" onClick={(event) => { event.stopPropagation(); void showDetail(partner); }}><Eye /></button>{canManage && <button className="icon-button" title="Uredi partnera" onClick={(event) => { event.stopPropagation(); open(partner); }}><Pencil /></button>}<ChevronRight /></span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!items.length && <div className="table-empty">Nema partnera u ovom prikazu.</div>}
        </div>}
        <MobileVirtualList
          className="partner-mobile-list"
          items={items}
          estimateSize={230}
          getKey={(partner) => partner.id}
          empty={<div className="table-empty">Nema partnera u ovom prikazu.</div>}
          renderItem={(partner) => (
            <SwipeReveal actionLabel={canManage ? 'Uredi' : 'Detalji'} ActionIcon={canManage ? Pencil : Eye} onAction={() => canManage ? open(partner) : void showDetail(partner)}>
              <article className="partner-mobile-card mobile-native-card" role="button" tabIndex={0} onClick={() => canManage ? open(partner) : void showDetail(partner)} onKeyDown={(event) => { if (event.key === 'Enter') canManage ? open(partner) : void showDetail(partner); }}>
                <header><span className="partner-avatar">{partner.name.slice(0, 2).toUpperCase()}</span><span><small>{partner.partnerCode}</small><strong>{partner.name}</strong><em>{[partner.address, partner.postalCode, partner.city].filter(Boolean).join(', ') || 'Adresa nije upisana'}</em></span><ChevronRight /></header>
                <div className="partner-mobile-card__contact">{partner.contactName && <strong>{partner.contactName}</strong>}{partner.email && <span><Mail /> {partner.email}</span>}{partner.phone && <span><Phone /> {partner.phone}</span>}</div>
                <footer><span>{partner.locationCount}<small>Lokacije</small></span><span>{partner.openTasks}<small>Zadaci</small></span><span>{partner.openWorkOrders}<small>Nalozi</small></span></footer>
              </article>
            </SwipeReveal>
          )}
        />
      </section>

      {modal && <div className="modal-backdrop"><form className="operational-modal" onSubmit={save}>
        <header><div><span className="eyebrow">ŠIFARNIK PARTNERA</span><h2>{editing ? 'Uredi partnera' : 'Novi partner'}</h2></div><button type="button" className="icon-button" onClick={() => setModal(false)}><X /></button></header>
        <div className="modal-scroll"><div className="form-grid">
          <label className="field"><span>Šifra partnera</span><input required value={form.partnerCode} onChange={(e) => setForm({ ...form, partnerCode: e.target.value })} /></label>
          <label className="field"><span>Naziv</span><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label className="field"><span>OIB</span><input inputMode="numeric" maxLength={11} value={form.oib} onChange={(e) => setForm({ ...form, oib: e.target.value.replace(/\D/g, '') })} /></label>
          <label className="field"><span>Kontakt osoba</span><input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} /></label>
          <label className="field field--full"><span>Adresa</span><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></label>
          <label className="field"><span>Poštanski broj</span><input value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} /></label>
          <label className="field"><span>Mjesto</span><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></label>
          <label className="field"><span>E-mail</span><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
          <label className="field"><span>Telefon</span><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
          <label className="field field--full"><span>Napomena</span><textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
          <label className="field"><span>Status</span><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="ACTIVE">Aktivan</option><option value="INACTIVE">Neaktivan</option></select></label>
        </div>{error && <div className="form-error">{error}</div>}</div>
        <footer><button type="button" className="button button--ghost" onClick={() => setModal(false)}>Odustani</button><button className="button button--primary" disabled={busy}>{busy ? 'Spremam…' : 'Spremi partnera'}</button></footer>
      </form></div>}

      {detail && <div className="drawer-backdrop" onClick={() => setDetail(null)}><aside className="company-drawer partner-drawer" onClick={(event) => event.stopPropagation()}>
        <header className="drawer-header"><div><span className="eyebrow">{detail.partnerCode}</span><h2>{detail.name}</h2></div><button className="icon-button" onClick={() => setDetail(null)}><X /></button></header>
        <div className="drawer-body"><section className="drawer-section"><h3>Kontakt i osnovni podaci</h3><p>{[detail.address, detail.postalCode, detail.city].filter(Boolean).join(', ') || 'Nema adrese'}</p>{detail.oib && <p><strong>OIB:</strong> {detail.oib}</p>}{detail.email && <p><Mail /> {detail.email}</p>}{detail.phone && <p><Phone /> {detail.phone}</p>}</section>
        <section className="drawer-section"><div className="section-title-row"><h3>Lokacije partnera</h3>{canManage && <button className="button button--primary button--small" onClick={() => setLocationModal(true)}><Plus /> Dodaj lokaciju</button>}</div><div className="location-list">{locations.map((location) => <article key={location.id}><MapPin /><div><strong>{location.name}</strong><small>{location.address}, {location.postalCode} {location.city}</small></div></article>)}{!locations.length && <p className="muted">Još nema dodatnih lokacija.</p>}</div></section></div>
      </aside></div>}

      {locationModal && detail && <div className="modal-backdrop"><form className="mini-modal" onSubmit={saveLocation}><header><h2>Nova lokacija</h2><button type="button" className="icon-button" onClick={() => setLocationModal(false)}><X /></button></header>
        <label className="field"><span>Naziv lokacije</span><input required value={locationForm.name} onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} /></label>
        <label className="field"><span>Adresa</span><input required value={locationForm.address} onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })} /></label>
        <div className="form-grid"><label className="field"><span>Poštanski broj</span><input value={locationForm.postalCode} onChange={(e) => setLocationForm({ ...locationForm, postalCode: e.target.value })} /></label><label className="field"><span>Mjesto</span><input required value={locationForm.city} onChange={(e) => setLocationForm({ ...locationForm, city: e.target.value })} /></label></div>
        <label className="field"><span>Kontakt osoba</span><input value={locationForm.contactName} onChange={(e) => setLocationForm({ ...locationForm, contactName: e.target.value })} /></label>
        <label className="field"><span>Telefon</span><input value={locationForm.contactPhone} onChange={(e) => setLocationForm({ ...locationForm, contactPhone: e.target.value })} /></label>
        <footer><button className="button button--primary" disabled={busy}>Spremi lokaciju</button></footer></form></div>}
    </div>
  );
}
